// Slice 40 — stroke-based icon set, currentColor-themed. Ported from
// `mockups/Vyvern HQ - superadmin/design_handoff_vyvern_hq/icons.jsx`,
// kept minimal — only the icons the Slice 40 shell + setup/login needs.
// Later slices append; do not delete (audited UI consumers).

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { sw?: number };

// Icons are decorative — the meaning is carried by the surrounding label
// (button text, sidebar nav label). `aria-hidden` keeps screen-readers from
// announcing the SVG node redundantly and satisfies biome's a11y/noSvgWithoutTitle.
const make =
  (path: React.ReactNode, viewBox = "0 0 24 24") =>
  ({ sw = 1.7, width = 16, height = 16, ...p }: IconProps) => (
    <svg
      viewBox={viewBox}
      width={width}
      height={height}
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...p}
    >
      {path}
    </svg>
  );

export const Icons = {
  Activity: make(<path d="M3 12h4l3-9 4 18 3-9h4" />),
  Key: make(
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l9-9M16 3l4 4M14 5l3 3" />
    </>,
  ),
  Inbox: make(
    <>
      <path d="M3 13l3-8h12l3 8v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M3 13h5l1 2h6l1-2h5" />
    </>,
  ),
  Users: make(
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M21.5 19a4.5 4.5 0 0 0-7-3.7" />
    </>,
  ),
  Telemetry: make(<path d="M4 19V5M4 19h16M8 15l3-4 3 2 4-6" />),
  Gavel: make(<path d="M14 2l8 8-4 4-8-8zM10 6l8 8M3 21l8-8M5 19l4 4" />),
  Audit: make(
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6M8 13h8M8 17h5" />
    </>,
  ),
  Settings: make(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>,
  ),
  Shield: make(<path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6z" />),
  Plus: make(<path d="M12 5v14M5 12h14" />),
  X: make(<path d="M6 6l12 12M18 6L6 18" />),
  Check: make(<path d="M4 12l5 5L20 6" />),
  ChevD: make(<path d="M6 9l6 6 6-6" />),
  Lock: make(
    <>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>,
  ),
  Logout: make(
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>,
  ),
  Search: make(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </>,
  ),
  Revoke: make(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M6 6l12 12" />
    </>,
  ),
  Copy: make(
    <>
      <rect x="8" y="8" width="13" height="13" rx="2" />
      <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
    </>,
  ),
  Download: make(<path d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />),
  Sparkles: make(
    <>
      <path d="M12 4v6m0 4v6M4 12h6m4 0h6" />
      <path d="M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
    </>,
  ),
  Tag: make(
    <>
      <path d="M20.6 12.6l-8-8A2 2 0 0 0 11.2 4H5a2 2 0 0 0-2 2v6.2c0 .5.2 1 .6 1.4l8 8a2 2 0 0 0 2.8 0l6.2-6.2a2 2 0 0 0 0-2.8z" />
      <circle cx="7.5" cy="7.5" r="1" />
    </>,
  ),
  Image: make(
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="1.8" />
      <path d="M21 17l-5-5-9 9" />
    </>,
  ),
  EyeOff: make(
    <path d="M3 3l18 18M10.6 6.1A10 10 0 0 1 22 12c-1 2-2.5 3.7-4.4 4.9M6.4 6.6C4.1 8 2.5 10 2 12c2 4 6 7 10 7 1.4 0 2.7-.3 3.9-.8M9.9 9.9a3 3 0 0 0 4.2 4.2" />,
  ),
  Eye: make(
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </>,
  ),
  Send: make(<path d="M3 12l18-9-7 9 7 9z" />),
};
